import Grid from '@mui/material/Grid'
import { useElplanInfo } from 'api/elplan/elplan.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import globalStore from 'store/global/global'
import { getTabFromStorage } from 'ui/atoms/Tabs/getTabFromStorage'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import View from 'ui/components/View/View'
import { useSnapshot } from 'valtio'
import { DayAheadBidManagement } from './components/DayAheadBidManagement/DayAheadBidManagement'
import { DayAheadBidTableRouteBlocker } from './components/DayAheadBidTable/useDayAheadBidTableRouteBlocker'
import DayAheadOverview from './components/DayAheadOverview/DayAheadOverview'
import DayAheadPlan from './components/DayAheadPlan/DayAheadPlan'
import { DayAheadPriceScenarios } from './components/DayAheadPriceScenarios/DayAheadPriceScenarios'
import { DayAheadTradingHeader } from './components/DayAheadTradingHeader'
import {
  getAccessGatedDayAheadTabs,
  getActivePartitionKey,
  hasPartitionScopedScenarios,
  shouldShowPartitionSwitch,
} from './DayAheadTradingView.helper'
import styles from './DayAheadTradingView.module.less'
import { type DayAheadTabDefinition, DayAheadTabId } from './DayAheadTradingView.types'
import { confirmDayAheadBidTableLeave } from './dayAheadBidTableLeave.helper'

const DAY_AHEAD_TABS_STORAGE_KEY = 'DayAheadTradingTabs'

// Mirrors ui/atoms/Tabs/Tabs.tsx's own resolution of the same storage key against its `items`
// prop, so the two never disagree about which tab a stored index means once tabs are access-filtered.
export function getInitialDayAheadTabId(tabs: DayAheadTabDefinition[]): DayAheadTabId {
  const savedIndex = getTabFromStorage(`activeTab_${DAY_AHEAD_TABS_STORAGE_KEY}`, 0)
  return tabs[savedIndex]?.id ?? tabs[0]?.id ?? DayAheadTabId.PriceScenarios
}

export function DayAheadTradingView(): ReactNode {
  const { t } = useTranslation()
  const { activeSystem, hasAccess } = useAuth()
  const hasDayAheadTradingAccess = useHasPermission('view_day_ahead_trading')
  const tabs = getAccessGatedDayAheadTabs(hasAccess, t)
  const [tabId, setTabId] = useState<DayAheadTabId>(() => getInitialDayAheadTabId(tabs))
  const [selectedPartitionKey, setSelectedPartitionKey] = useState('')
  const width = useSnapshot(globalStore).cardWidth
  const { data: elplanInfo } = useElplanInfo(activeSystem?.id, hasDayAheadTradingAccess)
  const partitions = elplanInfo?.partitions ?? []
  const activePartitionKey = getActivePartitionKey(partitions, selectedPartitionKey)

  useEffect(() => {
    if (selectedPartitionKey !== '' && !partitions.some(({ key }) => key === selectedPartitionKey)) {
      setSelectedPartitionKey('')
    }
  }, [partitions, selectedPartitionKey])

  const visibleTabIds = tabs.map(({ id }) => id)
  const showPartitionSwitch =
    hasDayAheadTradingAccess &&
    hasAccess({ module: 'optimize', submodule: 'optimize_subnet' }) &&
    partitions.length > 1 &&
    shouldShowPartitionSwitch(tabId, visibleTabIds, hasPartitionScopedScenarios(elplanInfo?.scenarios ?? []))

  return (
    <div className={styles.DayAheadTradingView}>
      <View
        stickyHeader
        header={
          <DayAheadTradingHeader
            tabs={tabs}
            showTabs={hasDayAheadTradingAccess && tabs.length > 0}
            onTabChange={(id) => setTabId(id as DayAheadTabId)}
            canChangeTab={() => confirmDayAheadBidTableLeave('tab')}
            showPartitionSwitch={showPartitionSwitch}
            partitionOptions={partitions.map(({ key, display_name }) => ({ key, displayName: display_name }))}
            selectedPartitionKey={activePartitionKey}
            onPartitionChange={(partitionKey) => {
              if (confirmDayAheadBidTableLeave('partition')) {
                setSelectedPartitionKey(partitionKey)
              }
            }}
          />
        }
      >
        {hasDayAheadTradingAccess && tabs.length > 0 ? (
          <div className={styles.DayAheadTradingView_Content}>
            {tabId === DayAheadTabId.PriceScenarios && visibleTabIds.includes(DayAheadTabId.PriceScenarios) && (
              <Grid size={{ xs: 12 }} width={width}>
                <DayAheadPriceScenarios selectedPartitionKey={activePartitionKey} />
              </Grid>
            )}
            {tabId === DayAheadTabId.BidManagement && visibleTabIds.includes(DayAheadTabId.BidManagement) && (
              <Grid size={{ xs: 12 }} width={width}>
                <DayAheadBidManagement selectedPartitionKey={activePartitionKey} />
              </Grid>
            )}
            {tabId === DayAheadTabId.Overview && visibleTabIds.includes(DayAheadTabId.Overview) && (
              <Grid size={{ xs: 12 }} width={width}>
                <DayAheadOverview />
              </Grid>
            )}
            {tabId === DayAheadTabId.Plan && visibleTabIds.includes(DayAheadTabId.Plan) && (
              <Grid size={{ xs: 12 }} width={width}>
                <DayAheadPlan />
              </Grid>
            )}
          </div>
        ) : (
          <div>{t('Unfortunately, you do not have permission to view this page')}</div>
        )}
      </View>
    </div>
  )
}

export function DayAheadTradingRoute(): ReactNode {
  return (
    <>
      <DayAheadBidTableRouteBlocker />
      <DayAheadTradingView />
    </>
  )
}
